/* eslint-disable react/no-array-index-key */
import { FC } from 'react';

import Button from 'molecules/Button';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { CapabilityCalloutConfigProps } from 'components/CapabilityCallout/config';

import { hasArrayValues } from 'utils/array/arrays';
import { buildBackgroundUrl } from 'utils/cloudinary/buildBackgroundUrl';

const CapabilityCallout: FC<CapabilityCalloutConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { backgroundImage, heading, supportingText, chips, button } = rest;
  const backgroundUrl = buildBackgroundUrl(backgroundImage?.file?.url);

  return (
    <Section {...sectionProps} sidePadding="none">
      {/* Grid stack: the KeyArt image and the card share the same cell and are both centered.
          The row grows to whichever is taller, so the image is never vertically cropped, and the
          card always sits centered over it. overflow-hidden clips the horizontal bleed on mobile. */}
      <div className="relative grid w-full grid-cols-1 place-items-center overflow-hidden px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
        {backgroundUrl && (
          // Mobile keeps the wide bleed (fixed width, center-cropped sides like the design). From
          // sm up the image fits the available width so the full motif shows with no side cropping.
          // Plain <img> is intentional: decorative background already optimized by buildBackgroundUrl.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={backgroundUrl}
            alt=""
            aria-hidden
            loading="lazy"
            className="pointer-events-none -z-10 col-start-1 row-start-1 h-auto w-[1393px] max-w-none sm:w-full sm:max-w-[1393px]"
          />
        )}

        <ScrollFadeSection delay={0.1} className="z-10 col-start-1 row-start-1 w-full">
          <div className="mx-auto flex w-full max-w-[1100px] flex-col items-start gap-12 rounded-lg bg-[linear-gradient(180deg,rgba(4,64,69,0.7)_0%,rgba(16,16,23,0.7)_100%)] px-6 py-10 backdrop-blur-[5px] sm:px-10 sm:py-14 lg:items-center lg:px-[50px] lg:py-[70px]">
            <div className="flex w-full flex-col items-start gap-10 lg:items-center">
              <div className="flex flex-col gap-1.5 text-left lg:items-center lg:text-center">
                <h2 className="font-spaceGrotesk text-3xl font-semibold leading-[38px] text-white lg:text-5xl lg:leading-[60px] lg:tracking-[-0.96px]">
                  {heading}
                </h2>
                {supportingText && (
                  <p className="font-roboto text-md leading-6 text-white lg:max-w-[666px] lg:text-xl lg:leading-[1.3]">
                    {supportingText}
                  </p>
                )}
              </div>

              {hasArrayValues(chips) && (
                <div className="flex w-full flex-wrap gap-2.5 lg:justify-center">
                  {chips!.map((chip, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-center rounded-[35px] border border-primary-50 px-6.5 py-0.5"
                    >
                      <span className="whitespace-nowrap font-roboto text-md font-semibold uppercase leading-[30px] text-primary-50">
                        {chip.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {button?.label && <Button {...button} hierarchy="primary" />}
          </div>
        </ScrollFadeSection>
      </div>
    </Section>
  );
};

export default CapabilityCallout;
